import {
  BreakoutRadioButton,
  BreakoutRadioGroup,
} from 'components/design-system/BreakoutRadioButton'
import { useMemo, useCallback, useId } from 'react'
import { WarningBox } from 'components/WarningBox'
import { BreakoutTooltip } from 'components/design-system/BreakoutTooltip'
import { BreakoutAlert } from 'components/design-system/BreakoutAlert'
import { useTranslationTyped } from 'i18n/i18n'
import { formatCentsToDollars } from '@breakoutlearning/firebase-repository/util'
import {
  FormLabel,
  HasNoAssociatedFormField,
} from 'components/design-system/form/FormLabel'

type ProductOption = {
  productId?: string
  productName?: string
  sectionPassPrice: number
  sectionPassQuantity: number
  isCustom: boolean
}

type LegacyProductOption = {
  sectionPassPrice: number
  sectionPassQuantity: number
  isCustom: true
  productName?: string
}

export function PricingSelectionWidget({
  customProducts,
  defaultProducts,
  legacyProductOption,
  selectedProduct,
  onSelectionChange,
  disabled = false,
  hasCorreOverride = false,
}: {
  customProducts: ProductOption[]
  defaultProducts: ProductOption[]
  legacyProductOption?: LegacyProductOption
  selectedProduct?: ProductOption
  onSelectionChange?: (selection: ProductOption) => void
  disabled?: boolean
  hasCorreOverride?: boolean
}) {
  const { tt } = useTranslationTyped()
  const labelId = useId()

  const allOptions = useMemo(() => {
    const all = [
      ...customProducts,
      ...defaultProducts,
      ...(legacyProductOption ? [legacyProductOption] : []),
    ]
    return all.sort((a, b) => {
      if (a.sectionPassPrice !== b.sectionPassPrice) {
        return a.sectionPassPrice - b.sectionPassPrice
      }
      return (a.productName ?? '').localeCompare(b.productName ?? '')
    })
  }, [customProducts, defaultProducts, legacyProductOption])

  const handleValueChange = useCallback(
    (value: string) => {
      const option = allOptions.find(
        (opt) =>
          ('productId' in opt ? (opt.productId ?? 'legacy') : 'legacy') ===
          value
      )
      if (option) {
        onSelectionChange?.(option)
      }
    },
    [allOptions, onSelectionChange]
  )

  if (allOptions.length === 0) {
    return null
  }

  const renderProductOption = (option: {
    productId?: string
    productName?: string
    sectionPassPrice: number
    sectionPassQuantity: number
    isCustom: boolean
  }) => {
    const displayPrice = formatCentsToDollars(option.sectionPassPrice)

    const displayQuantity =
      option.sectionPassQuantity === -1
        ? tt.instructor_library.unlimited_assignments()
        : option.sectionPassQuantity === 1
          ? tt.instructor_library.max_of_1_assignment()
          : tt.instructor_library.max_of_assignments({
              count: option.sectionPassQuantity,
            })

    return (
      <BreakoutTooltip
        enabled={disabled}
        content={tt.instructor_library.section_pricing_warning()}
        key={option.productId ?? 'legacy'}
      >
        <div
          className="grow"
          data-testid={`pricing-option-${option.productId ?? 'legacy'}`}
        >
          <BreakoutRadioButton
            value={option.productId ?? 'legacy'}
            label={
              <div className="flex flex-1 flex-col gap-1">
                <div className="whitespace-nowrap text-title-medium text-on-surface">
                  {option.productName ? option.productName + ' - ' : ''}$
                  {displayPrice}
                  {tt.instructor_library.per_student()}
                </div>
                <div className="text-body-medium text-on-surface-var">
                  {displayQuantity}
                </div>
              </div>
            }
            kind="tertiary"
            variant="compact"
          />
        </div>
      </BreakoutTooltip>
    )
  }

  return (
    <div className="flex flex-col gap-2" data-testid="pricing-field">
      <FormLabel
        id={labelId}
        label={tt.instructor_library.pricing()}
        required
        for={HasNoAssociatedFormField}
      />

      {!disabled && hasCorreOverride && (
        <WarningBox className="mt-4">
          <div className="text-body-medium">
            {tt.instructor_library.pricing_override_detected()}
          </div>
          <div className="text-body-small">
            {tt.instructor_library.pricing_override_detected_description()}
          </div>
        </WarningBox>
      )}

      <BreakoutRadioGroup
        required
        value={selectedProduct?.productId ?? 'legacy'}
        onValueChange={handleValueChange}
        disabled={disabled}
        className="flex flex-col gap-2"
        aria-labelledby={labelId}
      >
        {allOptions.map(renderProductOption)}
      </BreakoutRadioGroup>

      {selectedProduct?.isCustom && (
        <div>
          <div className="max-h-2 grow" />
          <BreakoutAlert variant="info">
            {tt.instructor_class.custom_pricing_notice()}
          </BreakoutAlert>
        </div>
      )}
    </div>
  )
}
